/* eslint-disable react/jsx-filename-extension */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import PropTypes from 'prop-types'
import axios from 'axios'
import localforage from 'localforage'
import env from 'config'
import OtpVerifyModal from 'components/OtpVerifyModal'

// 3-hour client-side "verified session" cache. When this elapses the
// admin is automatically logged out and redirected to the sign-in page —
// they have to enter password + login OTP again to continue.
const SESSION_MS = 3 * 60 * 60 * 1000
const STORAGE_KEY = 'admin_otp_verified_at'
// Flag the sign-in page can read to show "your session expired" UX.
const EXPIRED_FLAG = 'aloca_session_expired'

const BASE_URL = `${env.API_URL}/v1`

const OtpActionContext = createContext(null)
OtpActionContext.displayName = 'OtpActionContext'

function OtpActionProvider({ children }) {
  // Verified-at timestamp (ms). null = not verified / expired.
  const [verifiedAt, setVerifiedAt] = useState(null)

  // Modal state.
  const [open, setOpen] = useState(false)
  const [email, setEmail] = useState('')
  const [expiresAt, setExpiresAt] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  // The current "gate" promise. When `requireOtp()` is called for an action,
  // we stash a resolver here; the modal's verify/cancel handlers settle it.
  const pendingResolverRef = useRef(null)

  // Hydrate the session cache on mount so a refresh within 3 hours doesn't
  // re-prompt. We also persist on every successful verify.
  useEffect(() => {
    let cancelled = false
    localforage.getItem(STORAGE_KEY).then((stored) => {
      if (cancelled) return
      const ts = Number(stored)
      if (Number.isFinite(ts) && Date.now() - ts < SESSION_MS) {
        setVerifiedAt(ts)
      }
    })
    return () => {
      cancelled = true
    }
  }, [])

  // Sync across tabs: if another tab verifies, we pick it up here.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== STORAGE_KEY) return
      const ts = Number(e.newValue)
      if (Number.isFinite(ts) && Date.now() - ts < SESSION_MS) {
        setVerifiedAt(ts)
      } else if (!e.newValue) {
        setVerifiedAt(null)
      }
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  // Auto-logout on session expiry. As soon as the verified-at timestamp
  // is older than SESSION_MS, we wipe all admin storage and force a hard
  // redirect to /auth/sign-in. A hard redirect (not react-router navigate)
  // is intentional — it guarantees every in-memory provider (UserContext,
  // BrandContext, AgencyContext, MUI controller) is reset cleanly.
  useEffect(() => {
    if (!verifiedAt) return undefined

    const expireSession = async () => {
      try {
        const token = await localforage.getItem('token')
        // Already logged out (manual logout or initial mount) — nothing to do.
        if (!token) return

        await localforage.clear()
        try {
          window.localStorage.removeItem(STORAGE_KEY)
          // Sign-in page reads this to surface "your session expired" UX.
          window.sessionStorage.setItem(EXPIRED_FLAG, '1')
        } catch (e) {
          // sessionStorage / localStorage may be unavailable in some
          // environments — non-fatal, the redirect below still happens.
        }
        setVerifiedAt(null)
        window.location.assign('/auth/sign-in')
      } catch (e) {
        // Last-resort: force the redirect even if storage cleanup failed.
        window.location.assign('/auth/sign-in')
      }
    }

    const remaining = SESSION_MS - (Date.now() - verifiedAt)
    if (remaining <= 0) {
      expireSession()
      return undefined
    }
    const timer = setTimeout(expireSession, remaining)
    return () => clearTimeout(timer)
  }, [verifiedAt])

  const isSessionValid = useCallback(() => {
    if (!verifiedAt) return false
    return Date.now() - verifiedAt < SESSION_MS
  }, [verifiedAt])

  // Mark a fresh verification — used both by the OTP modal flow and by
  // the login page (which calls this after step 2 of login succeeds, so
  // the next 3 hours of action calls don't re-prompt).
  const markVerified = useCallback(async () => {
    const now = Date.now()
    setVerifiedAt(now)
    try {
      await localforage.setItem(STORAGE_KEY, now)
      // Trigger storage event for other tabs (localforage uses IndexedDB
      // which doesn't broadcast; we mirror to localStorage just for the
      // event signal).
      window.localStorage.setItem(STORAGE_KEY, String(now))
    } catch (e) {
      // Non-fatal — cache is in-memory at minimum.
    }
  }, [])

  const clearVerified = useCallback(async () => {
    setVerifiedAt(null)
    try {
      await localforage.removeItem(STORAGE_KEY)
      window.localStorage.removeItem(STORAGE_KEY)
    } catch (e) {
      // Non-fatal.
    }
  }, [])

  const getAuthToken = async () => {
    return localforage.getItem('token')
  }

  const sendActionOtp = async () => {
    const token = await getAuthToken()
    const res = await axios.post(
      `${BASE_URL}/auth/admin/sendActionOtp`,
      {},
      { headers: { 'x-auth-token': `Bearer ${token}` } }
    )
    return res.data
  }

  const verifyActionOtp = async (otp) => {
    const token = await getAuthToken()
    const res = await axios.post(
      `${BASE_URL}/auth/admin/verifyActionOtp`,
      { otp },
      { headers: { 'x-auth-token': `Bearer ${token}` } }
    )
    return res.data
  }

  // Public API: gate any sensitive client action behind an OTP.
  //
  // Returns a Promise<boolean> — true when OTP is verified (or the session
  // cache is still valid), false when the user cancels.
  //
  // The caller is expected to do nothing if false is returned (i.e. don't
  // fire the underlying request).
  const requireOtp = useCallback(async () => {
    if (isSessionValid()) return true

    // Open the modal and request the OTP. If the OTP send fails, surface
    // the error inside the modal and let the user retry.
    setBusy(true)
    setError('')
    setOpen(true)
    try {
      const sendRes = await sendActionOtp()
      if (!sendRes?.success) {
        setError(sendRes?.message || 'Could not send OTP. Try again.')
      } else {
        setEmail(sendRes?.data?.email || '')
        setExpiresAt(sendRes?.data?.otpExpiresAt || null)
      }
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Could not send OTP.')
    } finally {
      setBusy(false)
    }

    // Hand control to the modal — it will resolve this promise via the
    // verify/cancel handlers below.
    return new Promise((resolve) => {
      pendingResolverRef.current = resolve
    })
  }, [isSessionValid])

  const closeModal = useCallback(() => {
    setOpen(false)
    setError('')
    setExpiresAt(null)
    setEmail('')
  }, [])

  const handleVerify = async (otp) => {
    setBusy(true)
    setError('')
    try {
      const res = await verifyActionOtp(otp)
      if (!res?.success) {
        setError(res?.message || 'Invalid OTP.')
        setBusy(false)
        return
      }
      await markVerified()
      const resolver = pendingResolverRef.current
      pendingResolverRef.current = null
      closeModal()
      resolver?.(true)
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Verification failed.')
    } finally {
      setBusy(false)
    }
  }

  const handleResend = async () => {
    setBusy(true)
    setError('')
    try {
      const sendRes = await sendActionOtp()
      if (!sendRes?.success) {
        setError(sendRes?.message || 'Could not resend OTP.')
      } else {
        setEmail(sendRes?.data?.email || email)
        setExpiresAt(sendRes?.data?.otpExpiresAt || null)
      }
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Could not resend OTP.')
    } finally {
      setBusy(false)
    }
  }

  const handleCancel = () => {
    const resolver = pendingResolverRef.current
    pendingResolverRef.current = null
    closeModal()
    resolver?.(false)
  }

  const value = useMemo(
    () => ({
      requireOtp,
      isSessionValid,
      markVerified,
      clearVerified,
    }),
    [requireOtp, isSessionValid, markVerified, clearVerified]
  )

  return (
    <OtpActionContext.Provider value={value}>
      {children}
      <OtpVerifyModal
        open={open}
        email={email}
        title="Verify it's you"
        description="For your security, enter the 6-digit code we just emailed you to confirm this action."
        busy={busy}
        error={error}
        expiresAt={expiresAt}
        onVerify={handleVerify}
        onResend={handleResend}
        onClose={handleCancel}
      />
    </OtpActionContext.Provider>
  )
}

OtpActionProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

function useOtpAction() {
  const ctx = useContext(OtpActionContext)
  if (!ctx) {
    // Called outside the provider — fail open so the app doesn't deadlock.
    // This should never happen in production; provider is wired at the root.
    return {
      requireOtp: async () => true,
      isSessionValid: () => true,
      markVerified: async () => {},
      clearVerified: async () => {},
    }
  }
  return ctx
}

export { OtpActionProvider, useOtpAction }
